import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQueryClient } from 'react-query';
import { authAPI } from '../services/api';
import toast from 'react-hot-toast';
import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
} from '../utils/safeStorage';
import { setTeamTenantInfo, clearTeamTenantInfo, getTeamTenantInfo } from '../utils/tenantHelper';
import { applyVerifiedPlayerLanguage } from '../utils/playerUiLanguage';

/** Decode JWT payload (no verify) — use to read `type` when API body is ambiguous. */
export function readJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
    return JSON.parse(atob(b64 + pad));
  } catch {
    return null;
  }
}

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);

  useEffect(() => {
    // Check if user is logged in on app start
    checkAuthStatus();
    // Restore team tenant from localStorage
    const savedTenant = getTeamTenantInfo();
    if (savedTenant) {
      setTenant(savedTenant);
    }
  }, []);

  const checkAuthStatus = async () => {
    try {
      const token = getLocalStorageItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await authAPI.verifyToken();
      if (response.success && response.data) {
        const data = response.data;
        if (data.type === 'player') {
          const claims = readJwtPayload(token);
          setUser({
            type: 'player',
            id: data.playerId,
            playerId: data.playerId,
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            tenantId: data.tenantId,
            tenant: data.tenant,
            profileComplete: data.profileComplete !== false,
            impersonation: data.impersonation === true || claims?.impersonation === true,
            impersonatedBy: data.impersonatedBy || claims?.impersonatedBy || null,
          });
          applyVerifiedPlayerLanguage(data.uiLocale);
          if (data.tenant) {
            setTeamTenantInfo(data.tenant);
            setTenant(data.tenant);
          }
        } else {
          setUser({ ...data, id: data.teamId || data.id });
          try {
            const profileRes = await authAPI.getProfile();
            if (profileRes?.success && profileRes.data?.tenant) {
              setTeamTenantInfo(profileRes.data.tenant);
              setTenant(profileRes.data.tenant);
            }
          } catch (profileErr) {
            // Keep existing tenant from localStorage if profile fails
          }
        }
      } else {
        removeLocalStorageItem('token');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      removeLocalStorageItem('token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (loginData) => {
    setLoggingIn(true);
    console.log('🔐 AuthContext login called with:', loginData);
    console.log('📱 Device type:', navigator.userAgent.includes('Mobile') ? 'Mobile' : 'Desktop');
    
    try {
      const response = await authAPI.login(loginData);
      console.log('🌐 API response:', response);
      
      if (response.success && response.data) {
        const body = response.data;
        let { token, type, player, team, members, tenant: tenantData, redirectUrl } = body;
        if (!token) {
          console.error('Login response missing token');
          toast.error('Login failed. Invalid response.');
          return { success: false, message: 'Invalid response from server' };
        }
        const claims = readJwtPayload(token);
        const effectiveType = claims?.type || type;

        setLocalStorageItem('token', token);

        if (effectiveType === 'player') {
          const pid = player?.id || claims?.playerId;
          if (!pid) {
            console.error('Player login missing id', body, claims);
            toast.error('Login failed. Invalid player response.');
            return { success: false, message: 'Invalid response from server' };
          }
          let profileComplete = body.profileComplete !== false;
          if (tenantData) {
            setTeamTenantInfo(tenantData);
            setTenant(tenantData);
          }
          try {
            const vr = await authAPI.verifyToken();
            if (vr.success && vr.data?.type === 'player') {
              applyVerifiedPlayerLanguage(vr.data.uiLocale);
              profileComplete = vr.data.profileComplete !== false;
            }
          } catch {
            /* ignore */
          }
          setUser({
            type: 'player',
            id: pid,
            playerId: pid,
            email: player?.email || claims?.email,
            firstName: player?.firstName,
            lastName: player?.lastName,
            tenantId: player?.tenantId || claims?.tenantId,
            tenant: tenantData,
            profileComplete,
            impersonation: claims?.impersonation === true,
            impersonatedBy: claims?.impersonatedBy || null,
          });
          return {
            success: true,
            redirectUrl: redirectUrl || '/player',
            profileComplete,
            accountType: 'player',
            tenant: tenantData
          };
        }

        if (effectiveType === 'team') {
          const teamPayload =
            team ||
            (claims?.teamId
              ? {
                  id: claims.teamId,
                  teamName: claims.teamName,
                  email: claims.email
                }
              : null);
          if (!teamPayload) {
            console.error('Team login missing team payload', body, claims);
            toast.error('Login failed. Invalid response.');
            return { success: false, message: 'Invalid response from server' };
          }
          setUser({
            ...teamPayload,
            id: teamPayload.id || claims?.teamId,
            members,
            tenant: tenantData
          });
          if (tenantData) {
            setTeamTenantInfo(tenantData);
            setTenant(tenantData);
          }
          toast.success('Login successful!');
          return {
            success: true,
            redirectUrl: redirectUrl || '/dashboard',
            accountType: 'team',
            tenant: tenantData
          };
        }

        console.error('Login response shape not recognized', { body, claims, effectiveType });
        toast.error('Login failed. Invalid response.');
        return { success: false, message: 'Invalid response from server' };
      } else {
        console.log('❌ Login failed:', response.message);
        toast.error(response.message || 'Login failed');
        return { success: false, message: response.message };
      }
    } catch (error) {
      console.error('💥 Login error in AuthContext:', error);
      const serverMsg = error.response?.data?.message;
      const detail =
        Array.isArray(error.response?.data?.errors) && error.response.data.errors.length
          ? error.response.data.errors.join(' ')
          : null;
      const message = serverMsg || detail || 'Login failed. Please try again.';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoggingIn(false);
    }
  };

  const register = async (registerData) => {
    try {
      const response = await authAPI.register(registerData);
      if (response.success) {
        const { token, team, paymentRequired } = response.data;
        console.log('Registration response team data:', team);
        
        // Only auto-login if payment is not required
        if (!paymentRequired) {
          setLocalStorageItem('token', token);
          setUser(team);
          toast.success('Registration successful!');
        } else {
          // Don't auto-login if payment is required
          toast.success('Registration successful! Please complete payment to activate your account.');
        }
        
        return { success: true, data: response.data };
      } else {
        // Don't show toast for validation errors - let the form handle them
        console.log('Registration response (error):', response);
        return { success: false, message: response.message, field: response.field };
      }
    } catch (error) {
      console.error('Registration error:', error);
      
      // Check if it's an axios error response with specific error message
      if (error.response?.data?.message) {
        return { 
          success: false, 
          message: error.response.data.message, 
          field: error.response.data.field,
          errors: error.response.data.errors
        };
      }
      
      // Fallback for other errors
      toast.error('Registration failed. Please try again.');
      return { success: false, message: 'Registration failed' };
    }
  };

  const logout = () => {
    removeLocalStorageItem('token');
    clearTeamTenantInfo();
    queryClient.clear();
    setUser(null);
    setTenant(null);
    toast.success('Logged out successfully');
  };

  /** Establish player session from a support-impersonation JWT (no password). */
  const acceptSupportSessionToken = async (rawToken) => {
    const token = typeof rawToken === 'string' ? rawToken.trim() : '';
    if (!token) {
      return { success: false, message: 'Missing support session token' };
    }
    const claims = readJwtPayload(token);
    if (!claims || claims.type !== 'player' || !claims.playerId) {
      return { success: false, message: 'Invalid support session token' };
    }
    if (claims.impersonation !== true) {
      return { success: false, message: 'Not a support session token' };
    }
    setLocalStorageItem('token', token);
    try {
      const response = await authAPI.verifyToken();
      if (!response.success || !response.data || response.data.type !== 'player') {
        removeLocalStorageItem('token');
        return { success: false, message: 'Support session could not be verified' };
      }
      const data = response.data;
      setUser({
        type: 'player',
        id: data.playerId,
        playerId: data.playerId,
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        tenantId: data.tenantId,
        tenant: data.tenant,
        profileComplete: data.profileComplete !== false,
        impersonation: true,
        impersonatedBy: data.impersonatedBy || claims.impersonatedBy || null,
      });
      applyVerifiedPlayerLanguage(data.uiLocale);
      if (data.tenant) {
        setTeamTenantInfo(data.tenant);
        setTenant(data.tenant);
      }
      return { success: true, redirectUrl: '/player/tournaments' };
    } catch (error) {
      removeLocalStorageItem('token');
      const message =
        error.response?.data?.message || error.message || 'Support session failed';
      return { success: false, message };
    }
  };

  const updateUser = (updatedUser) => {
    setUser(prevUser => ({
      ...prevUser,
      ...updatedUser
    }));
  };

  const value = {
    user,
    tenant,
    loading,
    loggingIn,
    login,
    register,
    logout,
    acceptSupportSessionToken,
    updateUser,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
